import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Save, ArrowLeft, Loader2 } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { receiptReturnService } from '@/services/receiptReturnService'
import { financeService } from '@/services/financeService'
import { receiptsService } from '@/services/receiptsService'
import {
  ReceiptReturnVm,
  ReceiptReturnInvoice,
  ReceiptReturnInsertData,
  FinanceCustomer,
  ReceiptAccount,
} from '@/api/types'

export const ReceiptReturnsDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const branchId = user?.selectedBranch?.branchId

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loadingInsertData, setLoadingInsertData] = useState(false)

  // Form state
  const [formData, setFormData] = useState<{
    customerId: number
    invoiceId: number
    destinationAccount: number
    amount: number
    date: string
    note: string
    returnType: string
  }>({
    customerId: 0,
    invoiceId: 0,
    destinationAccount: 0,
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    note: '',
    returnType: 'Cash',
  })

  // Lookup data
  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [customerInvoices, setCustomerInvoices] = useState<ReceiptReturnInvoice[]>([])
  const [accounts, setAccounts] = useState<ReceiptAccount[]>([])
  const [insertData, setInsertData] = useState<ReceiptReturnInsertData | null>(null)

  // Return type options
  const returnTypeOptions = [
    { value: 'Cash', label: 'Cash' },
    { value: 'Bank', label: 'Bank' },
  ]

  // Load customers and accounts
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        setLoading(true)
        const [customersData, accountsData] = await Promise.all([
          financeService.getAllCustomers(branchId),
          receiptsService.getAllAccounts(branchId),
        ])

        setCustomers(customersData || [])
        setAccounts(accountsData || [])
      } catch (error) {
        console.error('Error loading lookups:', error)
        showSaveError(error as Error, false)
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId])

  // Load invoices when customer is selected
  useEffect(() => {
    if (!formData.customerId || formData.customerId === 0) {
      setCustomerInvoices([])
      setFormData(prev => ({ ...prev, invoiceId: 0 }))
      return
    }

    const loadInvoices = async () => {
      try {
        setLoading(true)
        const invoices = await receiptReturnService.getInvoicesByCustomerId(formData.customerId)
        setCustomerInvoices(invoices || [])
        
        // Reset invoice selection when customer changes
        setFormData(prev => ({ ...prev, invoiceId: 0 }))
        setInsertData(null)
      } catch (error) {
        console.error('Error loading invoices:', error)
        await showValidationError('Failed to load invoices')
      } finally {
        setLoading(false)
      }
    }

    loadInvoices()
  }, [formData.customerId])

  // Auto-fill form when invoice is selected
  useEffect(() => {
    if (!formData.invoiceId || formData.invoiceId === 0 || !branchId) {
      setInsertData(null)
      setFormData(prev => ({
        ...prev,
        destinationAccount: 0,
        amount: 0,
      }))
      return
    }

    const loadInsertData = async () => {
      try {
        setLoadingInsertData(true)
        const data = await receiptReturnService.getInsertData(branchId, formData.invoiceId)
        setInsertData(data)

        // Auto-fill form with insert data
        if (data && data.receiptReturn) {
          setFormData(prev => ({
            ...prev,
            destinationAccount: data.receiptReturn.destinationAccount || 0,
            amount: data.receiptReturn.amount || 0,
            date: data.receiptReturn.date ? new Date(data.receiptReturn.date).toISOString().split('T')[0] : prev.date,
            note: data.receiptReturn.note || '',
            returnType: data.receiptReturn.returnType || 'Cash',
          }))
        }
      } catch (error) {
        console.error('Error loading insert data:', error)
        await showValidationError('Failed to load receipt data')
      } finally {
        setLoadingInsertData(false)
      }
    }

    loadInsertData()
  }, [formData.invoiceId, branchId])

  // Load existing receipt return for edit mode
  useEffect(() => {
    if (isEditMode && id && branchId) {
      const loadReceiptReturn = async () => {
        try {
          setLoading(true)
          // TODO: Add getById API call when available
          // For now, this is a placeholder
        } catch (error: any) {
          console.error('Error loading receipt return:', error)
          await showSaveError(error, false)
          navigate('/receipt-returns')
        } finally {
          setLoading(false)
        }
      }

      loadReceiptReturn()
    }
  }, [isEditMode, id, branchId, navigate])

  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError('Please select a branch')
      return
    }

    if (!formData.customerId || formData.customerId === 0) {
      await showValidationError('Please select a customer')
      return
    }

    if (!formData.invoiceId || formData.invoiceId === 0) {
      await showValidationError('Please select an invoice')
      return
    }

    if (!formData.destinationAccount || formData.destinationAccount === 0) {
      await showValidationError('Please select destination account')
      return
    }

    if (!formData.amount || formData.amount <= 0) {
      await showValidationError('Please enter a valid amount')
      return
    }

    if (!formData.date) {
      await showValidationError('Please select a date')
      return
    }

    if (!formData.returnType) {
      await showValidationError('Please select return type')
      return
    }

    // Validate amount doesn't exceed max return amount
    if (insertData && formData.amount > insertData.maxReturnAmount) {
      await showValidationError(`Amount cannot exceed maximum return amount of ${insertData.maxReturnAmount.toFixed(2)}`)
      return
    }

    setSaving(true)

    try {
      const ipAddress = await getUserIPAddress()

      const saveData: ReceiptReturnVm = {
        id: isEditMode && id ? Number(id) : 0,
        invoiceId: formData.invoiceId,
        customerId: formData.customerId,
        destinationAccount: formData.destinationAccount,
        amount: formData.amount,
        date: new Date(formData.date).toISOString(),
        note: formData.note,
        returnType: formData.returnType,
        ipAddress: ipAddress,
      }

      console.log('🔵 Receipt Return Save Payload:', JSON.stringify(saveData, null, 2))

      if (isEditMode) {
        await receiptReturnService.updateReceiptReturn(saveData)
      } else {
        await receiptReturnService.saveReceiptReturn(saveData)
      }

      await showSaveSuccess(isEditMode, isEditMode ? 'Receipt return updated successfully' : 'Receipt return saved successfully')
      navigate('/receipt-returns')
    } catch (error: any) {
      console.error('Error saving receipt return:', error)
      const errorMessage = 
        error.response?.data?.message || 
        error.response?.data?.error || 
        error.message || 
        'Failed to save receipt return'
      await showSaveError(new Error(errorMessage), isEditMode)
    } finally {
      setSaving(false)
    }
  }

  // Prepare options for dropdowns
  const customerOptions: SearchableSelectOption[] = customers.map((cust) => ({
    value: String(cust.customerId || 0),
    label: cust.customerNameEn || cust.customerNameAr || `Customer ${cust.customerId}`,
  }))

  const invoiceOptions: SearchableSelectOption[] = customerInvoices.map((inv) => ({
    value: String(inv.invoiceId || 0),
    label: inv.invoiceNo || `Invoice ${inv.invoiceId}`,
  }))

  const accountOptions: SearchableSelectOption[] = accounts.map((acc) => {
    const accountId = acc.accountId || acc.id || 0
    const accountName = acc.accountNameEn || acc.accountName || acc.accountNameAr || ''
    const accountCode = acc.accountCode || ''
    const displayLabel = accountCode ? `${accountCode} - ${accountName}` : accountName
    return {
      value: String(accountId),
      label: displayLabel,
    }
  })

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  if (loading && !formData.customerId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={isEditMode ? 'Edit Receipt Return' : 'Add New Receipt Return'}
        description={isEditMode ? 'Update receipt return information' : 'Create a new receipt return'}
      >
        <Button variant="outline" onClick={() => navigate('/receipt-returns')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Return
        </Button>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>Receipt Return Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Customer and Invoice Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Customer Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="customer" className="text-gray-700 dark:text-gray-300 font-medium">
                Customer <span className="text-destructive">*</span>
              </Label>
              <SearchableSelect
                options={customerOptions}
                value={String(formData.customerId)}
                onValueChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    customerId: Number(value),
                    invoiceId: 0, // Reset invoice when customer changes
                  }))
                }}
                placeholder="Select customer"
                searchPlaceholder="Search customer..."
                emptyMessage="No customer found"
                disabled={saving || loading}
              />
            </div>

            {/* Invoice Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="invoice" className="text-gray-700 dark:text-gray-300 font-medium">
                Invoice <span className="text-destructive">*</span>
              </Label>
              <SearchableSelect
                options={invoiceOptions}
                value={String(formData.invoiceId)}
                onValueChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    invoiceId: Number(value),
                  }))
                }}
                placeholder={formData.customerId ? "Select invoice" : "Please select customer first"}
                searchPlaceholder="Search invoice..."
                emptyMessage={formData.customerId ? "No invoices found" : "Please select customer first"}
                disabled={saving || loading || !formData.customerId || customerInvoices.length === 0}
              />
              {loadingInsertData && formData.invoiceId > 0 && (
                <p className="text-sm text-muted-foreground">Loading receipt data...</p>
              )}
            </div>
          </div>

          {/* Other Fields in Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Destination Account Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="destinationAccount" className="text-gray-700 dark:text-gray-300 font-medium">
                Destination Account <span className="text-destructive">*</span>
              </Label>
              <SearchableSelect
                options={accountOptions}
                value={String(formData.destinationAccount)}
                onValueChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    destinationAccount: Number(value),
                  }))
                }}
                placeholder="Select destination account"
                searchPlaceholder="Search account..."
                emptyMessage="No account found"
                disabled={saving || loading}
              />
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount" className="text-gray-700 dark:text-gray-300 font-medium">
                Amount <span className="text-destructive">*</span>
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    amount: Number(e.target.value),
                  }))
                }}
                disabled={saving || loading}
                max={insertData?.maxReturnAmount}
              />
              {insertData && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Max return amount: {formatAmount(insertData.maxReturnAmount)}
                </p>
              )}
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="date" className="text-gray-700 dark:text-gray-300 font-medium">
                Date <span className="text-destructive">*</span>
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    date: e.target.value,
                  }))
                }}
                disabled={saving || loading}
              />
            </div>

            {/* Return Type */}
            <div className="space-y-2">
              <Label htmlFor="returnType" className="text-gray-700 dark:text-gray-300 font-medium">
                Return Type <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.returnType}
                onValueChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    returnType: value,
                  }))
                }}
                disabled={saving || loading}
              >
                <SelectTrigger id="returnType">
                  <SelectValue placeholder="Select return type" />
                </SelectTrigger>
                <SelectContent>
                  {returnTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Note - Full Width */}
          <div className="space-y-2">
            <Label htmlFor="note" className="text-gray-700 dark:text-gray-300 font-medium">
              Note
            </Label>
            <Textarea
              id="note"
              value={formData.note}
              onChange={(e) => {
                setFormData(prev => ({
                  ...prev,
                  note: e.target.value,
                }))
              }}
              disabled={saving || loading}
              placeholder="Enter note"
              rows={3}
            />
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/receipt-returns')}
              className="w-full sm:w-auto"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              className="w-full sm:w-auto"
              disabled={saving || loading}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditMode ? 'Update Receipt Return' : 'Save Receipt Return'}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

